import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { ListItemProps, MultiSelect, MultiSelectChangeEvent, MultiSelectHandle, TagData } from '@progress/kendo-react-dropdowns';
import { GridColumn as Column, Grid, GridCustomCellProps, GridItemChangeEvent, GridRowDoubleClickEvent, GridToolbar } from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import { TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Popover, PopoverActionsBar } from '@progress/kendo-react-tooltip';
import type { Polygon, Position } from '@trimblemaps/trimblemaps-js/geojson';
import { JsonResponse } from 'TypeGen/json-response';
import dayjs from 'dayjs';
import { cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import CenterDivPanel from '../../components/CenterDivPanel';
import GeofenceMap from '../../components/GeofenceMap';
import useAlert from '../../components/useAlert';
import useConfirm from '../../components/useConfirm';
import { fetchApi } from '../../services/api';

type Props = {
  RateMasterID: number;
  disabled?: boolean;
  className?: string;
  maxTier?: (value: number) => void;
};

type TierSummary = {
  TierGroups: TierGroup[];
  SupportedStates: string[];
};

type TierGroup = {
  TierID: number;
  ModifiedByFullName: string;
  ModifiedDateTime: Date;

  From: string[];
  To: string[];

  Comments: string;

  inEdit?: boolean | string;
  isNew?: boolean;
  Delete?: boolean;
};

type TierGroupSelectedOption = {
  TierID: number;
  IsFrom: boolean;
  IndexInArray: number;
}

interface EditCommandCellProps extends GridCustomCellProps {
  edit: (item: TierGroup) => void;
  remove: (item: TierGroup) => void;
  add: (item: TierGroup) => void;
  discard: () => void;
  update: (item: TierGroup) => void;
  cancel: (item: TierGroup) => void;
  editField: string;
}

const CommentCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  //let dataItem: TierGroup = props.dataItem;
  const value = props.dataItem[props.field] as string;

  const handleChange = (e: TextBoxChangeEvent) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value,
      });
    }
  };

  return (
    <td {...props.tdProps} style={{ textAlign: "left" }}>
      {props.dataItem.inEdit ? <TextBox
        value={value ?? ''}
        onChange={handleChange}
      /> :
        <span>{value}</span>
      }
    </td>
  );
}

const validateZipInput = (value: string) => {
  //const zipRegex = /^(?:\d{5}|[A-Za-z]\d[A-Za-z]\d[A-Za-z]\d)$/; //This one will validate against the full value
  //USA: USA: 3, 4, or 5 digits (e.g., 123, 1234, 12345)
  //CAN: 3 or 6 alternating alphanumeric characters (e.g., A1A, A1A1A1)
  const zipRegex = /^(?:\d{3,5}|[A-Za-z]\d[A-Za-z](?:\d[A-Za-z]\d)?)$/;
  return zipRegex.test(value);
};

const LastModifiedByCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const dataItem: TierGroup = props.dataItem;
  return (
    <td {...props.tdProps}>
      <span>{dayjs.utc(dataItem.ModifiedDateTime).tz("America/New_York").format("MM/DD/YYYY HH:mm")} - {dataItem.ModifiedByFullName}</span>
    </td>
  );
};

const Tier = (props: Props) => {
  const { alert } = useAlert();
  const { ConfirmationDialog, confirm } = useConfirm({});
  const [loading, setLoading] = useState(true);

  const editField: string = "inEdit";
  const [tierSummary, setTierSummary] = useState<TierSummary>();
  const [tierGroups, setTierGroups] = useState<TierGroup[]>([]);
  const [CSIDataCopy, setCSIDataCopy] = useState<TierGroup[]>([]);

  const MultiSelectCell = (props: GridCustomCellProps) => {
    const dataItem: TierGroup = props.dataItem;
    const values = props.dataItem[props.field] as string[];
    const anchor = useRef<MultiSelectHandle | null>(null);
    const [showPopover, setShowPopover] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [showVirtualTerminalPopover, setShowVirtualTerminalPopover] = useState(false);

    const [showZipRangeDialog, setShowZipRangeDialog] = useState<TierGroupSelectedOption | null>(null);
    const [showDrawGeofenceDialog, setShowDrawGeofenceDialog] = useState<TierGroupSelectedOption | null>(null);
    const [showVirtualTerminalDialog, setShowVirtualTerminalDialog] = useState<TierGroupSelectedOption | null>(null);

    const [zipRangeValue, setZipRangeValue] = useState<string[]>(["", ""]);
    const [polygonName, setPolygonName] = useState<string>('');
    const [polygonValue, setPolygonValue] = useState<Polygon | null>(null);

    const handleChange = (e: MultiSelectChangeEvent) => {
      if (props.onChange) {
        if ((e.value as string[]).some(x => x === "+ Zip Range")) {
          setShowZipRangeDialog({
            IsFrom: props.field === 'From',
            TierID: dataItem.TierID,
            IndexInArray: -1,
          });
        } else if ((e.value as string[]).some(x => x === "+ Draw Geofence")) {
          setShowDrawGeofenceDialog({
            IsFrom: props.field === 'From',
            TierID: dataItem.TierID,
            IndexInArray: -1,
          });
        } else if ((e.value as string[]).some(x => x === "+ Virtual Terminal")) {
          setShowVirtualTerminalDialog({
            IsFrom: props.field === 'From',
            TierID: dataItem.TierID,
            IndexInArray: -1,
          });
        } else {
          setShowZipRangeDialog(null);
          setShowDrawGeofenceDialog(null);
          setShowVirtualTerminalDialog(null);
          props.onChange({
            dataIndex: 0,
            dataItem: props.dataItem,
            field: props.field,
            syntheticEvent: e.syntheticEvent,
            value: e.value,
          });
        }
      }
    };

    useEffect(() => {
      setShowPopover(showZipRangeDialog?.TierID === dataItem?.TierID && ((showZipRangeDialog.IsFrom === true && props.field === 'From') || showZipRangeDialog.IsFrom === false && props.field === 'To'));
      setShowDialog(showDrawGeofenceDialog?.TierID === dataItem?.TierID && ((showDrawGeofenceDialog.IsFrom === true && props.field === 'From') || showDrawGeofenceDialog.IsFrom === false && props.field === 'To'));
      setShowVirtualTerminalPopover(showVirtualTerminalDialog?.TierID === dataItem?.TierID && ((showVirtualTerminalDialog.IsFrom === true && props.field === 'From') || showVirtualTerminalDialog.IsFrom === false && props.field === 'To'))
      if (showZipRangeDialog && showZipRangeDialog.IndexInArray === -1) {
        setZipRangeValue(["", ""]);
      }
      if (showDrawGeofenceDialog && showDrawGeofenceDialog.IndexInArray === -1) {
        setPolygonName('');
        setPolygonValue(null);
      }
    }, [dataItem?.TierID, props.field, showZipRangeDialog, showDrawGeofenceDialog, showVirtualTerminalDialog]);

    const UseItemRenderDisplayInfo = (itemRenderText: string, isInEditMode: boolean = false): [string, string] => {
      let itemRenderColor: string | undefined = undefined;

      if (itemRenderText.includes('POLYGON((')) {
        itemRenderText = itemRenderText.split('|')[0];
      } else if (itemRenderText.startsWith('VT:')) {
        itemRenderText = itemRenderText.split('VT:')[1];
        if (isInEditMode)
          itemRenderColor = itemRenderText === '0' ? '#004F00' : itemRenderText === '1' ? 'darkred' : 'yellow';
        else
          itemRenderColor = itemRenderText === '0' ? 'lightgreen' : itemRenderText === '1' ? '#FF4C4C' : 'yellow';
        itemRenderText = itemRenderText === '0' ? 'DEFICIT' : itemRenderText === '1' ? 'SURPLUS' : 'BALANCED';
      }

      return [itemRenderText, itemRenderColor];
    };

    const itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {

      const [itemRenderText, itemRenderColor] = UseItemRenderDisplayInfo(itemProps.dataItem);

      const itemChildren = <span style={{ color: itemRenderColor }}>{itemRenderText}</span>;
      return cloneElement(li, li.props, itemChildren);
    };

    const tagRender = (tagData: TagData, li: React.ReactElement<HTMLLIElement, string | React.JSXElementConstructor<any>>, TierID: number, IsFrom: boolean, IndexInArray: number) => {
      const handleTagClick = () => {
        if (tagData.text.includes('POLYGON((')) {
          // Open Geofence dialog
          const [name, polygon] = tagData.text.split('|');
          const coordinatesString = polygon
            .replace('POLYGON', '')
            .trim()
            .replace(/^\(+|\)+$/g, '');
          const coordinates = coordinatesString.split(',').map(coord => {
            return coord.trim().split(' ').map(Number) as Position;
          });
          setPolygonName(name);
          setPolygonValue({
            type: 'Polygon',
            coordinates: [coordinates]
          });
          setShowDrawGeofenceDialog({
            IsFrom: IsFrom,
            TierID: TierID,
            IndexInArray: IndexInArray,
          });
        } else if (tagData.text.includes('-')) {
          // Open Zip Range dialog
          const [zipStart, zipEnd] = tagData.text.split('-');
          setZipRangeValue([zipStart, zipEnd]);
          setShowZipRangeDialog({
            IsFrom: IsFrom,
            TierID: TierID,
            IndexInArray: IndexInArray,
          });
        }
      };

      const [itemRenderText, itemRenderColor] = UseItemRenderDisplayInfo(tagData.text, true);

      const tagChildren = <span onClick={handleTagClick} style={{ color: itemRenderColor }}>{itemRenderText}</span>;
      return cloneElement(li, {
        ...li.props,
        style: {
          ...(li.props.style),
          backgroundColor: "#A8A8A8",
          padding: "6px 9px"

        }
      }, tagChildren);
    };

    const setVirtualTerminal = (virtualTerminal: string) => {
      const tier = tierGroups.find(x => x.TierID == showVirtualTerminalDialog.TierID);

      const value = `VT:${virtualTerminal}`;

      if (showVirtualTerminalDialog.IndexInArray === -1) {
        if (showVirtualTerminalDialog.IsFrom) {
          tier.From.push(value);
        } else {
          tier.To.push(value);
        }
      } else {
        const arrIdx = showVirtualTerminalDialog.IndexInArray;
        if (showVirtualTerminalDialog.IsFrom) {
          tier.From[arrIdx] = value;
        } else {
          tier.To[arrIdx] = value;
        }
      }

      setTierGroups([...tierGroups]);

      setShowVirtualTerminalDialog(null)
    }

    const disableVirtualTerminalButton = (virtualTerminal: string) => {
      if (!showVirtualTerminalDialog)
        return false;
      const tier = tierGroups.find(x => x.TierID == showVirtualTerminalDialog.TierID);
      const arr = showVirtualTerminalDialog.IsFrom ? tier.From : tier.To;
      return arr.some(x => x.startsWith(`VT:${virtualTerminal}`));
    }

    return (
      <td {...props.tdProps} style={{ textAlign: "left" }}>
        {dataItem.inEdit ? <>
          <MultiSelect
            ref={anchor}
            data={tierSummary.SupportedStates}
            value={values}
            itemRender={itemRender}
            tagRender={(tagData, li) => tagRender(tagData, li, dataItem.TierID, props.field === 'From', values.indexOf(tagData.text))}
            onChange={handleChange}
          />
          <Popover show={showVirtualTerminalPopover} anchor={anchor.current && anchor.current.element} position={'bottom'} title={'Enter Virtual Terminal'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ButtonGroup>
                <Button
                  togglable={false}
                  disabled={disableVirtualTerminalButton("1")}
                  onClick={() => setVirtualTerminal("1")}
                  style={{ backgroundColor: "red", color: 'white', width: "66px", height: "30px", paddingLeft: "40px", paddingRight: "40px", fontWeight: "" }}
                >
                  SURPLUS
                </Button>
                <Button
                  togglable={false}
                  disabled={disableVirtualTerminalButton("2")}
                  onClick={() => setVirtualTerminal("2")}
                  style={{ backgroundColor: "yellow", color: 'black', width: "66px", height: "30px", paddingLeft: "45px", paddingRight: "45px", fontWeight: "" }}
                >
                  BALANCED
                </Button>
                <Button
                  togglable={false}
                  disabled={disableVirtualTerminalButton("0")}
                  onClick={() => setVirtualTerminal("0")}
                  style={{ backgroundColor: "green", color: 'white', width: "66px", height: "30px", paddingLeft: "40px", paddingRight: "40px", fontWeight: "" }}
                >
                  DEFICIT
                </Button>
              </ButtonGroup>
            </div>
            <PopoverActionsBar>
              <Button onClick={() => setShowVirtualTerminalPopover(null)} fillMode={'flat'}>Cancel</Button>
            </PopoverActionsBar>
          </Popover>
          <Popover show={showPopover} anchor={anchor.current && anchor.current.element} position={'bottom'} title={'Enter Zip Range'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TextBox
                value={zipRangeValue[0]}
                valid={validateZipInput(zipRangeValue[0])}
                maxLength={6}
                onChange={(e) => {
                  const rawValue = String(e.value);
                  const alphanumericValue = rawValue.replace(/[^a-zA-Z0-9]/g, '');
                  setZipRangeValue([alphanumericValue.toUpperCase(), zipRangeValue[1]]);
                }}
                placeholder={"e.g., 48180"}
              />
              -
              <TextBox
                value={zipRangeValue[1]}
                valid={validateZipInput(zipRangeValue[1])}
                maxLength={6}
                onChange={(e) => {
                  const rawValue = String(e.value);
                  const alphanumericValue = rawValue.replace(/[^a-zA-Z0-9]/g, '');
                  setZipRangeValue([zipRangeValue[0], alphanumericValue.toUpperCase()]);
                }}
                placeholder={"e.g., 48180"}
              />
            </div>
            <PopoverActionsBar>
              <Button onClick={() => {
                const tier = tierGroups.find(x => x.TierID == showZipRangeDialog.TierID);

                const tierInputValue = `${zipRangeValue[0]}-${zipRangeValue[1]}`;
                if (showZipRangeDialog.IndexInArray === -1) {
                  if (showZipRangeDialog.IsFrom) {
                    tier.From.push(tierInputValue);
                  } else {
                    tier.To.push(tierInputValue);
                  }
                } else {
                  const arrIdx = showZipRangeDialog.IndexInArray;
                  if (showZipRangeDialog.IsFrom) {
                    tier.From[arrIdx] = tierInputValue;
                  } else {
                    tier.To[arrIdx] = tierInputValue;
                  }
                }

                setTierGroups([...tierGroups]);

                const tierSummaryResult = tierSummary.SupportedStates.map(x => x);
                tierSummaryResult.splice(1, 0, tierInputValue);

                setTierSummary({ ...tierSummary, SupportedStates: [...tierSummaryResult] });

                setShowZipRangeDialog(null)


              }} themeColor={'primary'} fillMode={'flat'}>{showZipRangeDialog?.IndexInArray === -1 ? 'Insert' : 'Update'}</Button>
              <Button onClick={() => setShowZipRangeDialog(null)} fillMode={'flat'}>Cancel</Button>
            </PopoverActionsBar>
          </Popover>
          <Popover
            show={showDialog}
            anchor={anchor.current && anchor.current.element}
            position={'bottom'}
            title="Draw Geofence"
            contentStyle={{ padding: 0 }}
          >
            <TextBox
              valid={!!polygonName}
              placeholder='Geofence Name'
              value={polygonName}
              onChange={(e) => setPolygonName(e.value as string)}
            />
            <GeofenceMap
              style={{ height: 400, width: 500 }}
              value={polygonValue}
              onChange={(geometry) => setPolygonValue(geometry)}
            />
            <PopoverActionsBar>
              <Button
                themeColor='primary'
                fillMode='flat'
                disabled={!polygonName || !polygonValue}
                onClick={() => {
                  const value = `${polygonName}|POLYGON((${polygonValue.coordinates[0]
                    .map(c => c.map(coord => coord.toFixed(5)).join(' '))
                    .join(',')}))`;
                  const tier = tierGroups.find(x => x.TierID == showDrawGeofenceDialog.TierID);
                  if (showDrawGeofenceDialog.IndexInArray === -1) {
                    if (showDrawGeofenceDialog.IsFrom) {
                      tier.From.push(value);
                    } else {
                      tier.To.push(value);
                    }
                  } else {
                    const arrIdx = showDrawGeofenceDialog.IndexInArray;
                    if (showDrawGeofenceDialog.IsFrom) {
                      tier.From[arrIdx] = value;
                    } else {
                      tier.To[arrIdx] = value;
                    }
                  }

                  setShowDrawGeofenceDialog(null);
                }}
              >
                {showDrawGeofenceDialog?.IndexInArray === -1 ? 'Insert' : 'Update'}
              </Button>
              <Button
                fillMode='flat'
                onClick={() => setShowDrawGeofenceDialog(null)}
              >
                Cancel
              </Button>
            </PopoverActionsBar>
          </Popover>
        </> :
          <div style={{ maxWidth: '35px', display: 'flex', gap: '4px' }}>
            {values.map((x, key) => {
              const [itemRenderText, itemRenderColor] = UseItemRenderDisplayInfo(x);

              return (
                <span
                  key={key}
                  className="badge badge-dark p-2 font-weight-normal"
                  style={{ fontSize: '12px', color: itemRenderColor }}
                >
                  {itemRenderText}
                </span>
              );
            })}

            {/*{values.map((x, key) => (*/}
            {/*  const [itemRenderText, itemRenderColor] = UseItemRenderDisplayInfo(x);*/}

            {/*<span key={key} className="badge badge-dark p-2 font-weight-normal" style={{ fontSize: '12px' }}>{x.split('|')[0]}</span>*/}
            {/*))}*/}
          </div>
        }
      </td>
    );
  };

  const EditCommandCell = (_props: EditCommandCellProps) => {
    const inEdit = _props.dataItem[_props.editField];
    const dataItem: TierGroup = _props.dataItem;
    const isNewItem = dataItem.isNew;
    const disabled = dataItem.From.length === 0 || dataItem.To.length === 0;

    return inEdit ? (
      <td {..._props.tdProps} className={[_props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
        <Button themeColor={"primary"}
          disabled={disabled}
          onClick={() =>
            isNewItem ? _props.add(dataItem) : _props.update(dataItem)
          }
        >
          {isNewItem ? "Save" : "Update"}
        </Button>
        <Button themeColor={"secondary"}
          onClick={() =>
            isNewItem ? _props.discard() : _props.cancel(dataItem)
          }
        >
          {isNewItem ? "Discard" : "Cancel"}
        </Button>
      </td>
    ) : (
      <td {..._props.tdProps} className={[_props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
        <Button themeColor={"primary"}
          disabled={tierGroups.some(x => x.inEdit === true) || props.disabled === true}
          onClick={() => _props.edit(dataItem)}>Edit</Button>
        <Button themeColor={"secondary"}
          disabled={tierGroups.some(x => x.inEdit === true) || props.disabled === true}
          onClick={async () => {
            if (!await confirm(`Remove ?`))
              return;
            _props.remove(dataItem);
          }}>Delete</Button>
      </td>
    );
  };

  const MyEditCommandCell = (props: GridCustomCellProps) => (
    <EditCommandCell {...props}
      edit={enterEdit}
      remove={remove}
      add={add}
      discard={discard}
      update={update}
      cancel={cancel}
      editField={editField}
    />
  );

  const enterEdit = (dataItem: TierGroup) => {
    if (tierGroups.some(x => x.inEdit === true || props.disabled === true))
      return;

    setTierGroups(
      tierGroups.map((item) =>
        item.TierID === dataItem.TierID ? { ...item, inEdit: true } : item
      )
    );
  };

  const remove = (dataItem: TierGroup) => {
    updateTiers({ ...dataItem, Delete: true });
  };

  const add = (dataItem: TierGroup) => {
    updateTiers(dataItem);
  };

  const update = (dataItem: TierGroup) => {
    updateTiers(dataItem);
  };

  const cancel = (dataItem: TierGroup) => {
    const originalItem = CSIDataCopy.find(
      (p) => p.TierID === dataItem.TierID
    );
    const newData = tierGroups.map((item) =>
      item.TierID === originalItem.TierID ? cloneObject(originalItem) : item
    );

    setTierGroups(newData);
  };

  const generateId = () =>
    tierGroups.reduce((acc, current) => Math.max(acc, current.TierID), 0) + 1;

  const addNew = () => {
    const newDataItem = {
      inEdit: true, TierID: generateId(), isNew: true,
      From: [], To: [],
    } as TierGroup;
    setTierGroups([newDataItem, ...tierGroups]);
  };

  const discard = () => {
    const newData = [...tierGroups];
    newData.splice(0, 1);
    setTierGroups(newData);
  };


  const refresh = useCallback(() => {
    setLoading(true);

    fetchApi(`/api/Quote/GetTiers/${props.RateMasterID}`)
      .then((response: TierSummary) => {
        setTierSummary(response);
        setTierGroups(response.TierGroups);
        setCSIDataCopy(cloneObject(response.TierGroups)) //deep copy/clone        
        setLoading(false);
      })
      .catch(async e => {
        if (!e?.status)
          await alert('Error: Please see admin');
        else if (e.status !== 404) {
          await alert(e?.detail);
        }
        setLoading(false);
      });
  }, [props.RateMasterID, alert]);


  const { maxTier } = props;
  useEffect(() => {
    const maxTierID = tierGroups?.length
      ? Math.max(...tierGroups.map(x => x.TierID))
      : 0;
    maxTier?.(maxTierID);
  }, [tierGroups, maxTier]);

  const cloneObject = (obj: any): any => {
    return JSON.parse(JSON.stringify(obj));
  }

  const updateTiers = (dataItem: TierGroup) => {
    setLoading(true);

    const data = { ...dataItem }
    fetchApi(`api/Quote/Tier/${props.RateMasterID}`, data, 'POST')
      .then(async (response: JsonResponse) => {
        if (response.Success === false) {
          setLoading(false);
          await alert(`Error: ${response.ErrorMessage}`);
        }
        else {
          refresh();
        }
      })
      .catch(async e => {
        if (!e?.status)
          await alert('Error: Please see admin');
        else if (e.status !== 404) {
          await alert(e?.detail);
        }
        setLoading(false);
      });
  }

  useEffect(() => {
    refresh();
  }, [props.RateMasterID, refresh]);

  const itemChange = (event: GridItemChangeEvent) => {
    const field = event.field || "";
    const newData = tierGroups.map((item) =>
      item.TierID === event.dataItem.TierID
        ? { ...item, [field]: event.value }
        : item
    );

    setTierGroups(newData);
  }

  const dataView = () => {
    return (<div className={`${props.className ?? ''}`} style={{ position: "relative" }}>
      {loading && <CenterDivPanel>
        <Loader type="converging-spinner" />
      </CenterDivPanel>}
      <h4 className="text-left">Tiers</h4>
      <Grid
        style={{
          maxHeight: `${window.innerHeight * .35}px`,
        }}
        onRowDoubleClick={(e: GridRowDoubleClickEvent) => enterEdit(e.dataItem)}
        data={tierGroups}
        onItemChange={itemChange}
        dataItemKey="TierID"
        edit={Object.fromEntries(tierGroups.filter(x => x.inEdit).map(x => [x.TierID, true]))}
      >
        <GridToolbar>
          <Button themeColor={'primary'}
            disabled={tierGroups.some(x => x.inEdit === true) || props.disabled === true}
            title="Add new tier"
            onClick={addNew}
          >
            Add new tier
          </Button>
        </GridToolbar>
        <Column field="TierID" title="Tier" editable={false} />
        <Column field="From" title="From" cells={{ data: MultiSelectCell }} />
        <Column field="To" title="To" cells={{ data: MultiSelectCell }} />
        <Column field="Comments" title="Comment" cells={{ data: CommentCell }} />
        <Column field="ModifiedByFullName" title="Modified By" cells={{ data: LastModifiedByCell }} />
        <Column cells={{ data: MyEditCommandCell }} />
      </Grid>
      <ConfirmationDialog />
    </div >);
  };

  return dataView();
}
export default Tier;
